import { ConnectedRouter, routerMiddleware } from "connected-react-router";
import { createBrowserHistory } from "history";
import { NuqsAdapter } from "nuqs/adapters/react";
import { lazy, Suspense } from "react";
import ReactDOM from "react-dom";
import { QueryCache, ReactQueryCacheProvider } from "react-query";
import { Provider } from "react-redux";
import { Route, Switch } from "react-router-dom";
import { applyMiddleware, compose, createStore } from "redux";
import { apiMiddleware } from "redux-api-middleware";
import thunkMiddleware from "redux-thunk";
import { Loader } from "semantic-ui-react";
import EmployeeWrapper from "./components/EmployeeWrapper";
import { addMetaMiddleware, checkAuthMiddleware } from "./middlewares/auth";
import rootReducer from "./reducers";
import * as serviceWorker from "./serviceWorker";

import mixpanel from "mixpanel-browser";
import { ToastProvider } from "react-toast-notifications";
import "semantic-ui-css/semantic.min.css";
import ErrorBoundary from "./components/ErrorBoundary";
import Page404 from "./components/global_components/Page404";
import AppSettings from "./config";
import { NotifyApp } from "./features/notifications/NotifyApp";
import { Network } from "./lib/network";

import "./index.scss";
import "./semantic-ui-overrides.scss";
import { ToastWrapper } from "./components/global_components/Toast";

const App = lazy(() => import("./components/App"));
const Landing = lazy(() => import("./features/auth/pages/Landing"));
const Login = lazy(() => import("./features/auth/pages/Login"));
const SSO = lazy(() => import("./features/auth/pages/SSO"));
const StartSSO = lazy(() => import("./features/auth/pages/StartSSO"));
const Logout = lazy(() => import("./features/auth/pages/Logout"));
const Kickout = lazy(() => import("./features/auth/pages/Kickout"));
const ForgotAccount = lazy(() => import("./features/auth/pages/ForgotAccount"));
const ResetPassword = lazy(() => import("./features/auth/pages/ResetPassword"));
const Verify = lazy(() => import("./features/auth/pages/Verify"));
const PublicApp = lazy(() => import("./features/public/PublicApp"));
const CrmApp = lazy(() => import("./features/crm/CrmApp"));
const CodeApp = lazy(() => import("./features/ce/CodeApp"));
const CommunicationsApp = lazy(() => import("./features/notifications/CommunicationsApp"));
const PermitsApp = lazy(() => import("./features/permits/PermitsApp"));
const ContactsApp = lazy(() => import("./features/contacts/ContactsApp"));
const LocationsApp = lazy(() => import("./features/locations/LocationsApp"));
const AdminApp = lazy(() => import("./features/admin/AdminApp"));
const ToolsApp = lazy(() => import("./features/tools/ToolsApp"));
const TwitterOAuthCallback = lazy(() => import("./features/auth/pages/TwitterOAuthCallback"));
const InstagramOAuthCallback = lazy(() => import("./features/auth/pages/InstagramOAuthCallback"));
const FacebookOAuthCallback = lazy(() => import("./features/auth/pages/FacebookOAuthCallback"));
const MapScreenshotPage = lazy(() => import("./features/mapping/components/MapScreenshotPage"));

const history = createBrowserHistory();
const store = createStore(
  rootReducer(history),
  undefined,
  compose(
    applyMiddleware(
      thunkMiddleware, // dispatch()
      routerMiddleware(history), // For dispatching history actions
      addMetaMiddleware, // Add meta.endpoint to actions
      apiMiddleware, // Execute API calls
      checkAuthMiddleware, // Check for 401s and log out user
    ),
  ),
);
Network.initialize(store, history);
const queryCache = new QueryCache({
  defaultConfig: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const loader = <Loader active={true}>Loading...</Loader>;

mixpanel.init(AppSettings.MIXPANEL_TOKEN);

const routing = (
  <ErrorBoundary>
    <ReactQueryCacheProvider queryCache={queryCache}>
      <ToastProvider components={{ Toast: ToastWrapper }}>
        <Provider store={store}>
          <NuqsAdapter>
            <ConnectedRouter history={history}>
              <Suspense fallback={loader}>
                <Switch>
                  <Route path='/' exact component={App} />
                  <Route path='/:site/tools' component={ToolsApp} />
                  <Route path='/:site/landing' component={Landing} />
                  <Route path='/:site/public' component={PublicApp} />
                  <Route path='/:site/permits'>
                    <EmployeeWrapper render={() => <PermitsApp />} />
                  </Route>
                  <Route path='/:site/contacts'>
                    <EmployeeWrapper render={() => <ContactsApp />} />
                  </Route>
                  <Route path='/:site/locations'>
                    <EmployeeWrapper render={() => <LocationsApp />} />
                  </Route>
                  <Route path='/:site/communications'>
                    <EmployeeWrapper render={() => <CommunicationsApp />} />
                  </Route>
                  <Route path='/:site/notify'>
                    <EmployeeWrapper render={() => <NotifyApp />} />
                  </Route>
                  <Route path='/:site/code'>
                    <EmployeeWrapper render={() => <CodeApp />} />
                  </Route>
                  <Route path='/:site/crm'>
                    <EmployeeWrapper render={() => <CrmApp />} />
                  </Route>
                  <Route path='/:site/admin'>
                    <EmployeeWrapper render={() => <AdminApp />} />
                  </Route>
                  <Route path='/:site/forgot' component={ForgotAccount} />
                  <Route path='/:site/reset' component={ResetPassword} />
                  <Route path='/:site/welcome' component={Verify} />
                  <Route path='/:site/logout' component={Logout} />
                  <Route path='/:site/kickout' component={Kickout} />
                  <Route path='/:site/login' component={Login} />
                  <Route path='/oauth/callback' component={SSO} />
                  <Route path='/oauth2/twitter/callback' component={TwitterOAuthCallback} />
                  <Route path='/oauth2/instagram/callback' component={InstagramOAuthCallback} />
                  <Route path='/oauth2/facebook/callback' component={FacebookOAuthCallback} />
                  <Route path='/:site/oauth/start' component={StartSSO} />
                  <Route path='/:site/static-map-preview' component={MapScreenshotPage} />
                  <Route path='/:site' exact component={Login} />
                  <Route component={Page404} />
                </Switch>
              </Suspense>
            </ConnectedRouter>
          </NuqsAdapter>
        </Provider>
      </ToastProvider>
    </ReactQueryCacheProvider>
  </ErrorBoundary>
);

ReactDOM.render(routing, document.getElementById("root"));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
